import {
  Heading,
  LiveRegion,
  Stack,
  Text,
} from '@global-ecom/nitro-uds/elements';

import { ManagedHeading } from 'ui/components/ManagedHeading';
import { useFeature } from 'hooks/useFeature';
import { usePdp } from 'hooks/usePdp';
import { useTranslate } from 'hooks/useTranslations';
import { AfterpayOrKlarnaText } from 'ui/components/AfterpayOrKlarnaText';
import { ProductBadges } from 'ui/components/ProductBadge';
import { StarRating } from 'ui/components/StarRating';
import {
  PDP_PRICE_STRATEGY,
  ProductPrice,
} from 'ui/components/ProductPrice/ProductPrice';
import { Variant } from '__generated__/graphql';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { EmplifiStarsBelowPDPTitle } from 'ui/content/Emplifi/StarsBelowPDPTitle';

type ProductInfoProps = {
  rating?: string | number;
  reviewCount?: string | null;
};

export const ProductInfo = (props: ProductInfoProps) => {
  const showDynamicTaxMessage = useFeature('SHOW_DYNAMIC_TAX_MESSAGE');
  const translate = useTranslate();
  const {
    staticFeatures: { showHeaderSubHeaderProductDetail, isPNARegion },
  } = useSiteConfig();
  const { masterProduct, variation } = usePdp();

  const price = variation?.productPrice?.price;
  const salePrice = variation?.productPrice?.salePrice;
  const promotionPrice = variation?.productPrice?.promotionPrice;
  const isSalePriceElapsed = variation?.productPrice?.isSalePriceElapsed;

  const productName = showHeaderSubHeaderProductDetail
    ? masterProduct?.header
    : variation?.name;

  const shouldShowEmplifiStars = isPNARegion && !masterProduct?.disableRatings;
  const shouldShowDefaultStars = !isPNARegion && Number(props.reviewCount) >= 3;

  return (
    <Stack gap="sm">
      <Heading asChild size={{ _: 'base', md: 'lg', xl: 'xl' }}>
        <div className="grid gap-1" data-test-id="pdp-product-info-header">
          <ManagedHeading
            tag="h1"
            mainHeading={true}
            id="pdp-product-title"
            dataTestId="pdp-title"
            dangerouslySetInnerHTML={{ __html: productName! }}
          />
          {showHeaderSubHeaderProductDetail && masterProduct?.subHeader && (
            <h2
              data-test-id="pdp-product-subheader"
              className="w-full mobile:text-md font-normal text-lg text-puma-gray-60 line-clamp-2"
            >
              {masterProduct.subHeader}
            </h2>
          )}
        </div>
      </Heading>
      {shouldShowEmplifiStars && (
        <EmplifiStarsBelowPDPTitle sku={variation?.id ?? ''} />
      )}
      {shouldShowDefaultStars && (
        <StarRating
          dataTestId="rating-under-title"
          rating={props.rating}
          pageAnchorId="product-reviews"
        />
      )}
      <ProductBadges variant={variation as Variant} />
      {variation && variation.price > 0 && (
        <LiveRegion type="polite" atomic data-test-id="pdp-price-region">
          <Stack
            className="h-auto"
            data-test-id="pdp-price"
            items={{ _: 'center', lg: 'start' }}
            direction={{ _: 'row', lg: 'column' }}
            gap={{ _: 'sm', lg: 'xxs' }}
          >
            <Stack
              direction="row"
              items="center"
              className="w-auto"
              gap={{ _: 'sm', lg: 'xs' }}
            >
              <ProductPrice
                price={{
                  amount: price,
                  salePrice,
                  promotionPrice,
                  isSalePriceElapsed,
                }}
                compact
                {...PDP_PRICE_STRATEGY}
              />
            </Stack>
            {translate('priceClarification') && (
              <Text
                data-test-id="tax-clarification-text"
                size="sm"
                color="muted"
              >
                {showDynamicTaxMessage
                  ? variation.taxDisplayMsg
                  : translate('priceClarification')}
              </Text>
            )}
            <AfterpayOrKlarnaText amount={salePrice || price} />
          </Stack>
        </LiveRegion>
      )}
    </Stack>
  );
};
